import classnames from 'classnames';
import React, { useContext } from 'react';
import { useDeepEqualMemo } from './hooks/useDeepEqualMemo';
import { ThemeOptions, ThemeSkin, ThemeSurface } from './constants';

export function getGlobalClassnamesForContext({
  isMobile,
  theme,
  isSisu,
  flags = {},
  themeSkin,
  themeSurface = ThemeSurface.PRIMARY,
  isLoggedIn,
}: {
  isMobile?: boolean;
  theme?: ThemeOptions;
  themeSkin?: ThemeSkin;
  themeSurface?: ThemeSurface;
  isSisu?: boolean;
  flags?: {
    [k: string]: boolean;
  };
  isLoggedIn?: boolean;
}): string {
  return classnames(
    isMobile && 'mobile',
    !isSisu && `theme-${(theme || ThemeOptions.DARK).toString()}`,
    isSisu && 'sisu',
    themeSkin,
    'surface',
    themeSurface,
    flags['status_font_sizes'] && 'status_font_sizes',
    flags['selected_colors'] && 'selected-colors'
  );
}

export function getForcedGlobalClassnamesForContext({
  theme,
  themeSkin,
  themeSurface,
  flags = {},
}): string {
  return classnames(
    `theme-${theme}`,
    `forced-theme-${theme}`,
    themeSkin,
    'surface',
    themeSurface
  );
}

export function getComponentName(Component: React.ComponentType<any>) {
  return Component.displayName || Component.name || 'Component';
}

// Helper to create a context provider component that instead of replacing
// parent values, it merges them with the provided ones
export function createMergingContextProvider<T>(
  context: React.Context<T>,
  displayName = `${context.displayName || 'Context'}Provider`
) {
  const MergingContextProvider: React.FC<
    React.PropsWithChildren<{ value: T }>
  > = ({ children, value }) => {
    const parentContext = useContext(context);
    const merged = useDeepEqualMemo({ ...parentContext, ...value });

    return <context.Provider value={merged}>{children}</context.Provider>;
  };
  MergingContextProvider.displayName = displayName;

  return MergingContextProvider;
}
